import React from 'react';
import { useHistory } from 'react-router';
import { Button } from '@odin-labs/components';
import { useGetFormsQuery } from 'apollo/generated/client-operations';
import { useBoolean } from 'utils';
import { getGraphQLError } from 'utils/error';
import { AuthContext } from 'auth';
import { PlusIcon } from 'components/icons';
import { AlertService } from 'components/alertService';
import { JobsitePulseActivity } from 'containers/jobsite/tabs/jobsitePulse/types';
import { Link } from 'react-router-dom';
import {
  AddFormSubmissionModal,
  AddFormSubmissionModalProps,
} from 'containers/forms/modals';
import { getTestId, getTestIdAttr } from 'utils/testId';
import { ActivityFlag } from './ActivityFlag';
import { useJobsitePulse } from './JobsitePulseProvider';
import { snapshotPanelStyles } from './styles';

export function SnapshotActivityItem(
  props: JobsitePulseActivity,
): React.ReactElement {
  const {
    description,
    id,
    isIncident,
    label,
    testId = 'snapshot-activity',
  } = props;

  // TODO: [sc-13383] Update with correct URL based on document type
  const url = `/forms/site-safety/${id}/print`;

  return (
    <div
      className="odin-flex odin-flex-col odin-gap-1"
      {...getTestIdAttr(testId, 'root')}
    >
      <Link to={url} target="_blank">
        <ActivityFlag type={isIncident ? 'danger' : 'default'} text={label} />
      </Link>
      <p
        className="odin-text-slate-500 odin-line-clamp-3"
        {...getTestIdAttr(testId, 'description')}
      >
        {description}
      </p>
    </div>
  );
}

export function SnapshotActivityList(): React.ReactElement {
  const { activityList, loading: stateLoading } = useJobsitePulse();
  const loading = stateLoading?.jobsitePulse;

  if (loading) {
    return (
      <p
        className="odin-text-slate-500"
        {...getTestIdAttr('jobsite-activity-loading')}
      >
        Loading logs...
      </p>
    );
  }

  if (!activityList || activityList.length === 0) {
    return (
      <p
        className="odin-text-slate-500"
        {...getTestIdAttr('jobsite-no-activity')}
      >
        No logs
      </p>
    );
  }

  return (
    <div className="odin-flex odin-flex-col odin-gap-2">
      {activityList.map((activity) => (
        <SnapshotActivityItem
          key={`activity-${activity.id}`}
          testId={getTestId('jobsite-activity-item', activity.id)}
          {...activity}
        />
      ))}
    </div>
  );
}

export function SnapshotActivity(): React.ReactElement {
  const history = useHistory();
  const { currentUser: user } = React.useContext(AuthContext);
  const { jobsiteId } = useJobsitePulse();
  const {
    value: isAddFormSubmissionModalOpen,
    setTrue: openAddFormSubmissionModal,
    setFalse: closeAddFormSubmissionModal,
  } = useBoolean(false);

  const { data: formsData, loading: formsLoading } = useGetFormsQuery({
    fetchPolicy: 'no-cache',
    variables: { formsInput: { formKeys: ['site-safety'] } },
    onError: (error) => {
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    },
  });
  const [siteSafetyForm] =
    formsData?.getCurrentSession.user.forms.edges.map(({ node }) => node) ?? [];

  const closeAddFormSubmissionModalAndNavigateToFormSubmission: AddFormSubmissionModalProps['onConfirm'] =
    React.useCallback(
      (jobsiteFormSubmission): void => {
        closeAddFormSubmissionModal();
        history.push(`/forms/site-safety/${jobsiteFormSubmission.id}`);
      },
      [closeAddFormSubmissionModal, history],
    );

  return (
    <>
      <div className={snapshotPanelStyles.activity}>
        <div className="odin-flex odin-gap-4 odin-pt-1 odin-pr-1 odin-items-center">
          <h3
            className="odin-flex-1 odin-font-bold"
            {...getTestIdAttr('jobsite-activity-heading')}
          >
            Logs
          </h3>
          <Button
            theme="secondary"
            icon={PlusIcon}
            aria-label="Add incident"
            size="xs"
            onClick={openAddFormSubmissionModal}
            disabled={!user.hasFormsEnabled || !siteSafetyForm}
            withSpinner={formsLoading}
          />
        </div>
        <SnapshotActivityList />
      </div>
      <AddFormSubmissionModal
        isOpen={isAddFormSubmissionModalOpen}
        onCancel={closeAddFormSubmissionModal}
        onConfirm={closeAddFormSubmissionModalAndNavigateToFormSubmission}
        form={siteSafetyForm}
        jobsiteId={jobsiteId}
      />
    </>
  );
}
